import { storableError } from '../../util/errors';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { getWishlist, isArrayLength } from '../../util/genericHelpers';
import { getLocalWishlist } from '../../util/guestStorage';
import { queryListingsByIds } from '../../util/listingsByIds';

// Pagination page size might need to be dynamic on responsive page layouts
// Current design has max 3 columns 12 is divisible by 2 and 3
// So, there's enough cards to fill all columns on full pagination pages

const extraParams = {
  include: ['author', 'images', 'author.profileImage', 'currentStock'],
  'fields.listing': ['title', 'geolocation', 'price', 'publicData'],
  'fields.user': ['profile.displayName', 'profile.abbreviatedName', 'profile.publicData'],
  ...createImageVariantConfig(`listing-card`, 400, 1),
  ...createImageVariantConfig(`listing-card-2x`, 800, 1),
  'limit.images': 5,
};

// ================ Action types ================ //

export const SEARCH_LISTINGS_REQUEST = 'app/FavoritesPage/SEARCH_LISTINGS_REQUEST';
export const SEARCH_LISTINGS_SUCCESS = 'app/FavoritesPage/SEARCH_LISTINGS_SUCCESS';
export const SEARCH_LISTINGS_ERROR = 'app/FavoritesPage/SEARCH_LISTINGS_ERROR';

// ================ Reducer ================ //

const initialState = {
  pagination: null,
  searchParams: null,
  // Loading until the first search answers, so "no favorites" never flashes first.
  searchInProgress: true,
  searchListingsError: null,
  currentPageResultIds: [],
};

const resultInfiniteScrollIds = (data, oldData) => [...oldData, ...data.data.map(al => al.id)];

const listingPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case SEARCH_LISTINGS_REQUEST:
      return {
        ...state,
        searchParams: payload.searchParams,
        searchInProgress: true,
        currentPageResultIds: [],
        searchListingsError: null,
      };
    case SEARCH_LISTINGS_SUCCESS:
      let currentPageResultIds = resultInfiniteScrollIds(payload.data, state.currentPageResultIds);
      currentPageResultIds = currentPageResultIds.filter(
        (cpri, index, self) => index === self.findIndex(t => t.uuid === cpri.uuid)
      );
      return {
        ...state,
        currentPageResultIds,
        pagination: payload.data.meta,
        searchInProgress: false,
      };

    case SEARCH_LISTINGS_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, searchInProgress: false, searchListingsError: payload };

    default:
      return state;
  }
};

export default listingPageReducer;

// ================ Action creators ================ //

export const searchListingsRequest = searchParams => ({
  type: SEARCH_LISTINGS_REQUEST,
  payload: { searchParams },
});

export const searchListingsSuccess = response => ({
  type: SEARCH_LISTINGS_SUCCESS,
  payload: { data: response.data },
});

export const searchListingsError = e => ({
  type: SEARCH_LISTINGS_ERROR,
  error: true,
  payload: e,
});

export const searchListings = () => async (dispatch, getState, sdk) => {
  dispatch(searchListingsRequest());
  try {
    const { isAuthenticated } = getState().auth;

    // Guests keep their wishlist in localStorage; sdk.currentUser.show()
    // would reject for them.
    let wishlistIds;
    if (isAuthenticated) {
      const userResponse = await sdk.currentUser.show();
      wishlistIds = getWishlist(userResponse.data.data);
    } else {
      wishlistIds = getLocalWishlist();
    }

    if (!isArrayLength(wishlistIds)) {
      // Resolve to an empty result so searchInProgress doesn't stay stuck
      dispatch(searchListingsSuccess({ data: { data: [], meta: {} } }));
      return null;
    }

    // Every favorite, however many: one query takes 100 ids, so longer lists go in groups
    const response = await queryListingsByIds(sdk, wishlistIds, extraParams);
    dispatch(addMarketplaceEntities(response));
    dispatch(searchListingsSuccess(response));
    return response;
  } catch (e) {
    dispatch(searchListingsError(storableError(e)));
    throw e;
  }
};

export const loadData = () => {
  return searchListings();
};
